import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { GroupedProjectItems } from './ProjectOptionLabel';
import { ClipboardList, Clock, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const peso = (n) => `₱${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BillOfQuantitiesTab() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [boq, setBoq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);

  const loadProjects = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/api/supply-chain/boq/projects`); setProjects(r.data || []); }
    catch { toast.error('Failed to load projects'); }
  }, []);

  const loadBoq = useCallback(async (pid) => {
    if (!pid) { setBoq(null); return; }
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/api/supply-chain/boq/${pid}`); setBoq(r.data); }
    catch { toast.error('Failed to load bill of quantities'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadBoq(projectId); }, [projectId, loadBoq]);

  const refresh = useCallback(() => { loadProjects(); loadBoq(projectId); }, [loadProjects, loadBoq, projectId]);

  const togglePurchased = async (itemId, purchased) => {
    if (!itemId) return;
    setBoq(b => ({ ...b, categories: b.categories.map(c => ({ ...c, items: c.items.map(it => it.id === itemId ? { ...it, purchased } : it) })) }));
    try { await axios.post(`${API_URL}/api/supply-chain/boq/${projectId}/purchased`, { item_id: itemId, purchased }); }
    catch { toast.error('Failed to update purchased status'); loadBoq(projectId); }
  };

  const updatePurchasedQty = (itemId, value) => {
    setBoq(b => ({ ...b, categories: b.categories.map(c => ({ ...c, items: c.items.map(it => it.id === itemId ? { ...it, purchased_quantity: value } : it) })) }));
  };
  const savePurchasedQty = async (itemId, value) => {
    if (!itemId) return;
    try { await axios.post(`${API_URL}/api/supply-chain/boq/${projectId}/purchased-qty`, { item_id: itemId, quantity: value === '' ? null : Number(value) }); }
    catch { toast.error('Failed to save purchased quantity'); loadBoq(projectId); }
  };

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', refresh); };
  }, [refresh]);

  const recent = projects.slice(0, 6);

  const downloadExcel = async () => {
    if (!projectId) return;
    setDownloadingXlsx(true);
    try {
      const r = await axios.get(`${API_URL}/api/supply-chain/boq/${projectId}/excel`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BOQ.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download BOQ Excel'); }
    finally { setDownloadingXlsx(false); }
  };

  return (
    <Card data-testid="boq-tab">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-orange-500" /> Bill of Quantities
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} data-testid="boq-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadExcel} disabled={!projectId || downloadingXlsx} data-testid="boq-download-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> {downloadingXlsx ? 'Preparing...' : 'Export Excel'}
            </Button>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-[280px]" data-testid="boq-project-selector">
                <SelectValue placeholder="Choose a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.length === 0
                  ? <SelectItem value="none" disabled>No projects with a BOM yet</SelectItem>
                  : <GroupedProjectItems projects={projects} valueKey="project_id" />}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Procurement reference — material quantities and cost without margin, extracted from each project's Bill of Materials.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {recent.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="boq-recent-projects">
            <span className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Recently edited:</span>
            {recent.map(r => (
              <button key={r.project_id} onClick={() => setProjectId(r.project_id)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${projectId === r.project_id ? 'bg-orange-500 text-white border-orange-500' : 'hover:bg-muted'}`}
                data-testid={`boq-recent-${r.project_id}`}>{r.project_name}</button>
            ))}
          </div>
        )}

        {!projectId ? (
          <p className="text-sm text-muted-foreground py-10 text-center border rounded-md">Select a project to view its bill of quantities.</p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Loading...</p>
        ) : !boq || boq.categories.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center border rounded-md">No items encoded in this project's BOM.</p>
        ) : (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-xs border-collapse min-w-[720px]">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-2 border border-slate-600 w-20">Item No.</th>
                  <th className="p-2 border border-slate-600 text-left">Scope of Work</th>
                  <th className="p-2 border border-slate-600 w-24">Quantity</th>
                  <th className="p-2 border border-slate-600 w-20">Unit</th>
                  <th className="p-2 border border-slate-600 w-36">Material Cost</th>
                  <th className="p-2 border border-slate-600 w-28">Qty Purchased</th>
                  <th className="p-2 border border-slate-600 w-24">Remaining</th>
                  <th className="p-2 border border-slate-600 w-24">Purchased</th>
                </tr>
              </thead>
              <tbody>
                {boq.categories.map((cat, ci) => (
                  <React.Fragment key={ci}>
                    <tr className="bg-slate-100 dark:bg-slate-800 font-bold">
                      <td className="p-2 border" colSpan={4}>{cat.name || 'UNCATEGORIZED'}</td>
                      <td className="p-2 border text-right" data-testid={`boq-cat-total-${ci}`}>{peso(cat.category_material_cost)}</td>
                      <td className="p-2 border"></td>
                      <td className="p-2 border"></td>
                      <td className="p-2 border"></td>
                    </tr>
                    {cat.items.length === 0 ? (
                      <tr><td colSpan={8} className="p-2 border text-center text-muted-foreground">No items</td></tr>
                    ) : cat.items.map((it, ri) => {
                      if (it.row_type === 'section') {
                        return (
                          <tr key={it.id || ri} className="bg-slate-700 text-white font-bold" data-testid={`boq-section-${it.id || ri}`}>
                            <td className="p-2 border border-slate-500 text-center">{it.item_no}</td>
                            <td className="p-2 border border-slate-500" colSpan={3}>{it.scope || 'SECTION'}</td>
                            <td className="p-2 border text-right">{peso(it.material_cost)}</td>
                            <td className="p-2 border" colSpan={3}></td>
                          </tr>
                        );
                      }
                      if (it.row_type === 'task') {
                        return (
                          <tr key={it.id || ri} className="bg-amber-50 dark:bg-amber-950/20 font-semibold" data-testid={`boq-task-${it.id || ri}`}>
                            <td className="p-2 border text-center">{it.item_no}</td>
                            <td className="p-2 border pl-6" colSpan={3}>{it.scope || '—'}</td>
                            <td className="p-2 border text-right">{peso(it.material_cost)}</td>
                            <td className="p-2 border" colSpan={3}></td>
                          </tr>
                        );
                      }
                      const planned = Number(it.quantity) || 0;
                      const purchasedQty = Number(it.purchased_quantity) || 0;
                      const remaining = planned - purchasedQty;
                      const remClass = remaining < 0 ? 'text-red-600 font-semibold' : remaining === 0 ? 'text-emerald-600 font-semibold' : 'text-amber-600';
                      return (
                      <tr key={it.id || ri} className={it.purchased ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'hover:bg-muted/40'}>
                        <td className="p-2 border text-center text-muted-foreground">{it.item_no}</td>
                        <td className={`p-2 border pl-12 ${it.purchased ? 'line-through text-muted-foreground' : ''}`}>{it.scope || '—'}</td>
                        <td className="p-2 border text-center">{it.quantity}</td>
                        <td className="p-2 border text-center">{it.unit || '—'}</td>
                        <td className="p-2 border text-right">{peso(it.material_cost)}</td>
                        <td className="p-2 border text-center">
                          <input type="number" min="0" value={it.purchased_quantity ?? ''} disabled={!it.id}
                            onChange={e => updatePurchasedQty(it.id, e.target.value)}
                            onBlur={e => savePurchasedQty(it.id, e.target.value)}
                            placeholder="0"
                            className="w-20 h-7 text-xs text-center border rounded px-1 bg-background"
                            data-testid={`boq-purchased-qty-${it.id || ri}`} />
                        </td>
                        <td className={`p-2 border text-center ${remClass}`} data-testid={`boq-remaining-${it.id || ri}`}>{remaining}</td>
                        <td className="p-2 border text-center">
                          <input type="checkbox" checked={!!it.purchased} disabled={!it.id}
                            onChange={e => togglePurchased(it.id, e.target.checked)}
                            className="w-4 h-4 cursor-pointer accent-emerald-600"
                            data-testid={`boq-purchased-${it.id || ri}`} />
                        </td>
                      </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-800 text-white font-bold">
                  <td colSpan={4} className="p-2 border border-slate-600 text-right">TOTAL MATERIAL COST (no margin)</td>
                  <td className="p-2 border border-slate-600 text-right" data-testid="boq-grand-total">{peso(boq.total_material_cost)}</td>
                  <td colSpan={3} className="p-2 border border-slate-600"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
